import { useState } from "react";
import { CheckCircle2, Download, Eye, RotateCw } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { fechaHora } from "@/lib/format";
import {
  ALTAS, CANAL, COMPROBAR_SI_LLEGO, OPERACION, RESULTADO_INTENTO, bSinCodigo, enPruebas, esFinal, estadoComunicacion,
  faltaCodigoSes, gestionadaConAnulacion,
} from "@/lib/registro";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Lo enviado al entorno de pruebas de los Mossos (`environment: "PRE"`): no es una comunicación real.
export function MarcaPruebas({ c }) {
  if (!enPruebas(c)) return null;
  return (
    <Badge className="bg-[#E89B33]/15 text-[#7A4E0E] border-0" data-testid={`com-${c.id}-pruebas`}
      title="Enviada al entorno de pruebas de los Mossos: no cuenta como comunicada">
      Pruebas
    </Badge>
  );
}

// Qué ha cambiado en una modificación (`changes` de la API, en castellano y sin valores): lo que hay que comunicar en
// el formulario de Canal Empresa.
export function ListaCambios({ c }) {
  const cambios = c?.operation === "modification" && Array.isArray(c.changes) ? c.changes.filter(Boolean) : [];
  if (cambios.length === 0) return null;
  return (
    <div className="mt-2 text-sm" data-testid={`com-${c.id}-cambios`}>
      <span className="font-medium text-[#1A1A1A]">Qué ha cambiado:</span>
      <ul className="list-disc pl-5 text-[#5C5C5C]">{cambios.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </div>
  );
}

export async function descargarXml(c) {
  let resp;
  try {
    resp = await api.get(`/admin/registry/communications/${c.id}/xml`, { responseType: "blob" });
  } catch (err) {
    // Con `responseType: "blob"` el error de FastAPI también llega como Blob: se lee para enseñar su `detail`.
    const data = err?.response?.data;
    if (data instanceof Blob && typeof data.text === "function") {
      try { err.response.data = JSON.parse(await data.text()); } catch { /* sin un detalle legible */ }
    }
    throw err;
  }
  const nombre = /filename="([^"]+)"/.exec(resp.headers?.["content-disposition"] || "")?.[1]
    || `ses-${c.reservation_code}.xml`;
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const COMUNICADA = { boton: "Comunicada a mano", titulo: "¿La has comunicado ya por otra vía?",
  ok: "Sí, ya está comunicada", aviso: "Marcada como comunicada a mano" };
const HECHA = { boton: "Marcar como hecha", titulo: "¿Ya está hecha?", ok: "Sí, ya está hecha", aviso: "Marcada como hecha" };
const NOTA = "Comunicada a mano desde el panel";

// La confirmación de «Comunicada a mano» dice lo mismo que la instrucción de su fila (`Problemas` y los motivos de
// backend/registry/service.py), y deja una nota de qué se hizo.
function confirmacion(c) {
  const mossos = c.channel === "mossos";
  if (c.operation === "cancellation") {
    if (mossos) {
      const texto = "Márcala como hecha cuando hayas anulado la comunicación en el formulario de Canal Empresa de los Mossos.";
      // La alta quedó en duda (`manual.uncertain`): puede que no haya nada que anular; lo mismo que dice su fila.
      if (c.manual?.uncertain) {
        return { ...HECHA, nota: "Anulada en el formulario de Canal Empresa, o la alta no llegó a los Mossos",
          texto: `${texto} ${COMPROBAR_SI_LLEGO}` };
      }
      return { ...HECHA, nota: "Anulada en el formulario de Canal Empresa", texto };
    }
    if (c.status === "manual_required") { // MANUAL_SES_DOWNLOADED: se descargó un XML que pudo subirse
      return { ...HECHA, nota: "Anulada en SES.HOSPEDAJES o el XML no se llegó a subir",
        texto: "Si llegaste a subir el XML a SES.HOSPEDAJES, márcala cuando lo hayas anulado allí; si no llegaste a "
          + "subirlo, márcala como hecha: no hay nada que anular." };
    }
    return { ...COMUNICADA, nota: "Anulada a mano en SES.HOSPEDAJES",
      texto: "Márcala solo si ya has anulado la comunicación en el portal de SES.HOSPEDAJES: no se volverá a pedir." };
  }
  if (!mossos) {
    return { ...COMUNICADA, nota: NOTA,
      texto: "Márcala solo si ya la has comunicado en el portal de SES.HOSPEDAJES: no se volverá a pedir." };
  }
  if (c.operation === "modification") {
    return { ...COMUNICADA, nota: "Cambio comunicado en el formulario de Canal Empresa",
      texto: "Márcala solo si ya has comunicado el cambio en el formulario de Canal Empresa (Lloguer de vehicles)." };
  }
  if (c.status === "sent") {
    return { ...COMUNICADA, nota: "Consta en el portal de Canal Empresa",
      texto: "Márcala solo si consta en el portal de comunicaciones de Canal Empresa: se deja de consultar." };
  }
  if (c.status === "error") { // puede que llegara (envío sin respuesta o interrumpido)
    return { ...COMUNICADA, nota: NOTA,
      texto: "Márcala solo si consta en el portal de comunicaciones de Canal Empresa o la has comunicado en su "
        + "formulario web (Lloguer de vehicles): no se volverá a enviar." };
  }
  return { ...COMUNICADA, nota: NOTA,
    texto: "Márcala solo si ya la has comunicado en el formulario web de Canal Empresa (Lloguer de vehicles): no se enviará." };
}

const BOTON_SES = { presentar: "Marcar como presentada", codigo: "Añadir el código de SES",
  original: "Añadir el código de SES de la reserva presentada" };
const TITULO_SES = { presentar: "Presentada en SES.HOSPEDAJES", codigo: "Código de SES.HOSPEDAJES",
  original: "Código de SES de la reserva presentada" };

// Acciones de una comunicación según su estado (especificación §6): ver lo enviado, reintentar, descargar y
// presentar el XML de SES (o añadir después el código de SES), o dejar constancia de que se hizo a mano.
// - La que se está enviando (`sending`) solo se ve: la API no deja tocarla.
// - `anulada` (por defecto, `reservation_cancelled` de la API): una alta de una reserva anulada no se reintenta, y la
//   de los Mossos que llegó sin admitirse se gestiona con su anulación a mano (solo se ve).
// - La anulación (B) de SES que espera el código de la reserva presentada lo añade a esa (`original_id`).
// - Sin `admin` (el operador), ninguna: ver lo enviado lleva los datos de los dos conductores, y reintentar, descargar
//   el XML, presentar y marcar a mano son actos ante la autoridad (backend/roles.py).
export default function CommunicationActions({ c, onDone, anulada = Boolean(c?.reservation_cancelled), admin = true }) {
  const ses = c.channel === "ses";
  const modo = ses && c.status === "ready" ? "presentar" : faltaCodigoSes(c) ? "codigo" : bSinCodigo(c) ? "original" : null;
  const [detalle, setDetalle] = useState(null);
  const [presentar, setPresentar] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [lote, setLote] = useState(modo === "original" ? "" : c.ses?.lote || "");
  const [codigo, setCodigo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const base = `/admin/registry/communications/${c.id}`;
  const nombre = `${OPERACION[c.operation] || c.operation} de ${c.reservation_code}`;
  const soloVer = c.status === "sending" || gestionadaConAnulacion(c, anulada);
  const reintentable = ["rejected", "error", "pending_data"].includes(c.status) && !(anulada && ALTAS.includes(c.operation));
  const aMano = confirmacion(c);
  const hacer = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      onDone?.();
      return true;
    } catch (err) {
      toast.error(formatError(err));
      return false;
    }
  };
  const ver = async () => {
    try { setDetalle((await api.get(base)).data); } catch (err) { toast.error(formatError(err)); }
  };
  // El reintento vuelve a prepararla: «Preparada de nuevo» solo si ha quedado lista; si no, por qué.
  const reintentar = async () => {
    try {
      const { data } = await api.post(`${base}/retry`);
      if (data?.status === "ready") toast.success("Preparada de nuevo");
      else {
        const motivo = [...(data?.missing || []), ...(data?.validation_errors || [])].join("; ");
        toast.error(`Sigue pendiente: ${motivo || "vuelve a cargarla para ver su estado"}`);
      }
      onDone?.();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const guardar = async () => {
    setGuardando(true);
    const destino = modo === "original" ? `/admin/registry/communications/${c.original_id}` : base;
    const ok = await hacer(() => api.post(`${destino}/filed`, { lote: lote.trim(), codigo: codigo.trim() }), "Guardado");
    setGuardando(false);
    if (ok) setPresentar(false);
  };
  if (!admin) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={ver} aria-label={`Ver: ${nombre}`} data-testid={`com-${c.id}-ver`}>
        <Eye className="h-4 w-4 mr-1" /> Ver
      </Button>
      {!soloVer && reintentable && (
        <Button size="sm" variant="outline" onClick={reintentar} aria-label={`Reintentar: ${nombre}`} data-testid={`com-${c.id}-retry`}>
          <RotateCw className="h-4 w-4 mr-1" /> Reintentar
        </Button>
      )}
      {!soloVer && ses && c.status === "ready" && (
        <Button size="sm" variant="outline" onClick={() => hacer(() => descargarXml(c), "XML descargado")}
          aria-label={`Descargar XML: ${nombre}`} data-testid={`com-${c.id}-xml`}>
          <Download className="h-4 w-4 mr-1" /> Descargar XML
        </Button>
      )}
      {!soloVer && modo && (
        <Button size="sm" onClick={() => setPresentar(true)} className="bg-marca hover:bg-marca-hover text-white"
          aria-label={`${BOTON_SES[modo]}: ${nombre}`} data-testid={`com-${c.id}-filed`}>
          {BOTON_SES[modo]}
        </Button>
      )}
      {!soloVer && !esFinal(c) && (
        <Button size="sm" variant="ghost" onClick={() => setConfirmar(true)} aria-label={`${aMano.boton}: ${nombre}`}
          data-testid={`com-${c.id}-manual`}>
          <CheckCircle2 className="h-4 w-4 mr-1" /> {aMano.boton}
        </Button>
      )}
      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{aMano.titulo}</AlertDialogTitle>
            <AlertDialogDescription>{aMano.texto}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`com-${c.id}-manual-no`}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => hacer(() => api.post(`${base}/manual-done`, { note: aMano.nota }), aMano.aviso)}
              className="bg-marca hover:bg-marca-hover" data-testid={`com-${c.id}-manual-ok`}>
              {aMano.ok}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {presentar && modo && (
        <Dialog open onOpenChange={() => setPresentar(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{TITULO_SES[modo]}</DialogTitle>
              <DialogDescription className="text-sm text-[#5C5C5C]">
                {modo === "original"
                  ? "Anota el lote y el código de comunicación que dio SES a la reserva presentada: con el código se genera esta anulación."
                  : c.operation === "cancellation"
                    ? "Anota el lote y el código de comunicación que te da SES."
                    : "Anota el lote y el código de comunicación que te da SES. Sin el código no se podrá generar la anulación si la reserva se anula o cambia."}
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label htmlFor={`com-${c.id}-lote`}>Lote</Label>
              <Input id={`com-${c.id}-lote`} value={lote} onChange={(e) => setLote(e.target.value)} maxLength={36} data-testid={`com-${c.id}-lote`} />
            </div>
            <div>
              <Label htmlFor={`com-${c.id}-codigo`}>Código de comunicación</Label>
              <Input id={`com-${c.id}-codigo`} value={codigo} onChange={(e) => setCodigo(e.target.value)} maxLength={36} data-testid={`com-${c.id}-codigo`} />
            </div>
            <Button onClick={guardar} disabled={guardando || (modo !== "presentar" && !codigo.trim())}
              className="bg-marca hover:bg-marca-hover text-white" data-testid={`com-${c.id}-guardar`}>
              Guardar
            </Button>
          </DialogContent>
        </Dialog>
      )}
      {detalle && (
        <Dialog open onOpenChange={() => setDetalle(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                <span>
                  {OPERACION[detalle.operation] || detalle.operation} · {CANAL[detalle.channel] || detalle.channel} · {estadoComunicacion(detalle)}
                </span>
                <MarcaPruebas c={detalle} />
              </DialogTitle>
            </DialogHeader>
            <ListaCambios c={detalle} />
            {detalle.rejection && (
              <p className="text-sm text-[#8C3B26]">{detalle.rejection.description || (detalle.rejection.errors || []).join("; ")}</p>
            )}
            {(detalle.attempts || []).length > 0 && (
              <ul className="text-xs text-[#5C5C5C]">
                {detalle.attempts.map((a, i) => (
                  <li key={i}>
                    {fechaHora(a.at)} · {RESULTADO_INTENTO[a.outcome] || a.outcome}{a.http_status ? ` (código ${a.http_status})` : ""}
                  </li>
                ))}
              </ul>
            )}
            <pre className="bg-[#F7F5F0] p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">
              {detalle.payload ? JSON.stringify(detalle.payload, null, 2) : detalle.payload_xml || "Aún no se ha generado"}
            </pre>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
